import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import { RefreshCw, BarChart3, Building2, ChevronLeft, ChevronRight } from 'lucide-react';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function AccountBillReport() {
  const [partners, setPartners] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [partnerId, setPartnerId] = useState('all');
  const [orgId, setOrgId] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/reports/partners')
      .then(({ data: d }) => setPartners(d.partners || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const params = partnerId !== 'all' ? `?partner_id=${partnerId}` : '';
    api.get(`/reports/orgs${params}`)
      .then(({ data: d }) => setOrgs(d.orgs || []))
      .catch(() => {});
  }, [partnerId]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (partnerId !== 'all') params.append('partner_id', partnerId);
      if (orgId !== 'all') params.append('org_id', orgId);
      const { data: d } = await api.get(`/reports/account-bill-summary?${params.toString()}`);
      setData(d);
    } catch {
      console.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [partnerId, orgId]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Build org rows from data
  const orgMap = {};
  if (data?.months) {
    for (const m of data.months) {
      for (const o of m.orgs) {
        if (!orgMap[o.customer_uid]) {
          orgMap[o.customer_uid] = {
            uid: o.customer_uid,
            name: o.customer_name,
            partner_name: o.partner_name,
            months: {},
          };
        }
        orgMap[o.customer_uid].months[`${m.year}-${m.month}`] = {
          accounts: o.accounts,
          bills: o.bills,
        };
      }
    }
  }
  const orgRows = Object.values(orgMap).sort((a, b) => a.name.localeCompare(b.name));
  const months = data?.months || [];

  // Totals per month
  const monthTotals = months.map(m => ({
    key: `${m.year}-${m.month}`,
    accounts: m.orgs.reduce((s, o) => s + o.accounts, 0),
    bills: m.orgs.reduce((s, o) => s + o.bills, 0),
  }));

  return (
    <div className="border border-border rounded-none bg-card" data-testid="account-bill-report">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-5 w-5 text-primary" />
            <div>
              <h3 className="text-base font-bold">Account & Bill Summary</h3>
              <p className="text-xs text-muted-foreground">Accounts / Bills processed per organisation per month</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="rounded-none h-8" onClick={fetchReport} disabled={loading} data-testid="refresh-ab-report">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <Select value={partnerId} onValueChange={(v) => { setPartnerId(v); setOrgId('all'); }}>
            <SelectTrigger className="w-56 rounded-none h-8 text-xs" data-testid="ab-partner-filter">
              <Building2 className="h-3 w-3 mr-1 text-muted-foreground" />
              <SelectValue placeholder="All Partners" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All Partners</SelectItem>
              {partners.map(p => (
                <SelectItem key={p.id} value={String(p.id)}>{p.name} ({p.customer_count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger className="w-64 rounded-none h-8 text-xs" data-testid="ab-org-filter">
              <SelectValue placeholder="All Organisations" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All Organisations</SelectItem>
              {orgs.map(o => (
                <SelectItem key={o.id} value={String(o.id)}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wide sticky left-0 bg-muted/50 z-10 min-w-[200px]">
                Organisation
              </th>
              {months.map(m => (
                <th key={`${m.year}-${m.month}`}
                    className="text-center px-3 py-2.5 font-medium text-xs uppercase tracking-wide min-w-[100px]">
                  <div>{MONTHS[m.month - 1]}</div>
                  <div className="text-[10px] text-muted-foreground font-normal">{m.year}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && !data ? (
              <tr>
                <td colSpan={13} className="text-center p-8 text-muted-foreground">
                  <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading...
                </td>
              </tr>
            ) : orgRows.length === 0 ? (
              <tr>
                <td colSpan={13} className="text-center p-8 text-muted-foreground text-sm">
                  No data found for the selected filters
                </td>
              </tr>
            ) : (
              <>
                {orgRows.map(org => (
                  <tr key={org.uid} className="hover:bg-muted/30" data-testid={`ab-row-${org.uid}`}>
                    <td className="px-4 py-2 font-medium sticky left-0 bg-card z-10">
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="truncate block max-w-[200px] cursor-default">{org.name}</span>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="rounded-none text-xs">
                            <p className="font-medium">{org.name}</p>
                            <p className="text-muted-foreground">{org.partner_name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                    {months.map(m => {
                      const key = `${m.year}-${m.month}`;
                      const cell = org.months[key];
                      if (!cell || (cell.accounts === 0 && cell.bills === 0)) {
                        return (
                          <td key={key} className="text-center px-3 py-2 text-muted-foreground/40 tabular-nums">
                            -
                          </td>
                        );
                      }
                      return (
                        <td key={key} className="text-center px-3 py-2 tabular-nums">
                          <span className="text-blue-600 font-medium">{cell.accounts.toLocaleString()}</span>
                          <span className="text-muted-foreground mx-0.5">/</span>
                          <span className="font-medium">{cell.bills.toLocaleString()}</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* Totals row */}
                <tr className="bg-muted/40 font-bold border-t-2 border-border">
                  <td className="px-4 py-2 sticky left-0 bg-muted/40 z-10 text-xs uppercase tracking-wide">Total</td>
                  {monthTotals.map(mt => (
                    <td key={mt.key} className="text-center px-3 py-2 tabular-nums">
                      {mt.accounts === 0 && mt.bills === 0 ? (
                        <span className="text-muted-foreground/40">-</span>
                      ) : (
                        <>
                          <span className="text-blue-600">{mt.accounts.toLocaleString()}</span>
                          <span className="text-muted-foreground mx-0.5">/</span>
                          <span>{mt.bills.toLocaleString()}</span>
                        </>
                      )}
                    </td>
                  ))}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="px-4 py-2 border-t border-border flex items-center gap-4 text-[10px] text-muted-foreground">
        <span>Format: <span className="text-blue-600 font-medium">Accounts</span> / <span className="font-medium text-foreground">Bills</span></span>
        {data?.months?.some(m => m.from_cache) && (
          <span className="text-emerald-600">Past months served from cache</span>
        )}
      </div>
    </div>
  );
}
